
// React Imports
import * as React from "react";
import {useState, useEffect, useRef} from "react";
import {useDispatch, useSelector} from "react-redux";

// Mui Imports
import {
    DataGrid,
    GridToolbarColumnsButton,
    GridToolbarContainer, GridToolbarDensitySelector, GridToolbarExport,
    GridToolbarFilterButton
} from '@mui/x-data-grid';
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Box from "@mui/material/Box";
import SearchIcon from '@mui/icons-material/Search';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import {
    Avatar, Checkbox, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
    ListItemIcon, ListItemText, Menu, Stack,
    Tooltip
} from "@mui/material";
import ForwardIcon from '@mui/icons-material/Forward';
import ListIcon from '@mui/icons-material/List';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import NotInterestedIcon from '@mui/icons-material/NotInterested';
import HelpIcon from '@mui/icons-material/Help';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';


// App Imports

import {
    addGuildContracts, addGuildContractsBulk,
    deleteProduct, productSearch, updateCoveredProduct
} from "./ProductClient";

import MenuItem from "@mui/material/MenuItem";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {changeProduct} from "./productSlice";
import {changeCrudSnackbar} from "../../CommonComponents/crudSnackbarSlice";
import ProductDrawer from "./ProductDrawer";
import CustomFooter from "../../CommonComponents/GridCustomFooter";
import CrudSnackbar from "../../CommonComponents/CrudSnackbar";
import {changeUploadFile} from "../../CommonComponents/UploadFile/uploadFileSlice";
import Button from "@mui/material/Button";
import UploadFileDrawer from "../../CommonComponents/UploadFile/UploadFileDrawer";
import ProductSearch from "./ProductSearch";
import AddIcon from "@mui/icons-material/Add";
import Divider from "@mui/material/Divider";
import GuildMultiSelect from "../../Menus/GuildMultiSelect";
import LinearIndeterminate from "../../CommonComponents/Progress";
import {Link, useNavigate} from "react-router-dom";
import AddFiltersExportToolbar from "../../CommonComponents/AddFiltersExportToolbar";
import {ProductGridColumns} from "./ProductGridColumns";
import {changeWorkQueue} from "../../WorkQueue/workQueueSlice";
import ProductGuildDialogue from "./ProductGuildDialogue";
import {changeExternalData} from "../externalDataSlice";
import NoSearchCriteriaSnackbar from "../../CommonComponents/NoSearchCriteriaSnackbar";
import ProductInfoDialogue from "./Info/ProductInfoDialogue";

// main function
export default function ProductGrid() {

    // ****************** BEGIN STATES ****************************

    const [products, setProducts] = useState([]);
    const [deleteOpen, setDeleteOpen] = React.useState(false);
    const [gridVisible, setGridVisible] = React.useState(true);
    const [showControls, setShowControls] = useState(true);
    const [showSearch, setShowSearch] = useState(false);
    const hideActions = localStorage.getItem('hideActions') === "true";
    const [productInfoOpen, setProductInfoOpen] = useState(false);

    // ****************** END STATES ****************************


    // *********************** BEGIN STORE ********************************

    const productSearchCriteria = useSelector((state) => state.productSearchSlc.value.searchData);
    const selectorEidr = useSelector((state) => state.productSearchSlc.value.eidr);
    const selectorCoveredProduct = useSelector((state) => state.productSearchSlc.value.coveredProduct);
    const selectorEpisodeName = useSelector((state) => state.productSearchSlc.value.episodeName);
    const selectorExternalId = useSelector((state) => state.productSearchSlc.value.externalId);
    const selectorFinancialCode = useSelector((state) => state.productSearchSlc.value.financialCode);
    const selectorGenre = useSelector((state) => state.productSearchSlc.value.genre);
    const selectorInitMkt = useSelector((state) => state.productSearchSlc.value.initMkt);
    const selectorM4mktGroup = useSelector((state) => state.productSearchSlc.value.m4mktGroup);
    const selectorProducer = useSelector((state) => state.productSearchSlc.value.producer);
    const selectorProductionType = useSelector((state) => state.productSearchSlc.value.productionType);
    const selectorName = useSelector((state) => state.productSearchSlc.value.name);
    const selectorPhotoStartDateFrom = useSelector((state) => state.productSearchSlc.value.photoStartDateFrom);
    const selectorPhotoStartDateTo = useSelector((state) => state.productSearchSlc.value.photoStartDateTo);
    const selectorSeasonExternalId = useSelector((state) => state.productSearchSlc.value.seasonExternalId);
    const selectorSeriesExternalId = useSelector((state) => state.productSearchSlc.value.seriesExternalId);
    const selectorSeasonName = useSelector((state) => state.productSearchSlc.value.seasonName);
    const selectorSeriesName = useSelector((state) => state.productSearchSlc.value.seriesName);
    const selectorSeasonNumberFrom = useSelector((state) => state.productSearchSlc.value.seasonNumberFrom);
    const selectorSeasonNumberTo = useSelector((state) => state.productSearchSlc.value.seasonNumberTo);
    const selectorParticipations = useSelector((state) => state.productSearchSlc.value.participations);
    const selectorResiduals = useSelector((state) => state.productSearchSlc.value.residuals);
    const selectorRoyalties = useSelector((state) => state.productSearchSlc.value.royalties);

    const selectorProductId = useSelector((state) => state.productSlc.value.id);

    const selectorDrawerState = useSelector((state) => state.productSlc.value.productDrawerState);
    const selectorGuildMultiSelect = useSelector((state) => state.productSlc.value.guildMultiSelectState);

    const [submitting, setSubmitting] = useState(false);

    const selectedId = useSelector((state) => state.productSlc.value.id);

    const selectorUploadFileDrawerState = useSelector((state) => state.uploadFileSlc.value.uploadFileDrawerState);

    const dispatchData = useDispatch();

    const selectorProductGridRefresh = useSelector((state) => state.productSlc.value.productGridRefresh);

    const navigate = useNavigate(); // Hook to programmatically navigate

    // ********************** END STORE ********************************


    // ****************** BEGIN FETCH ****************************

    const fetchInProgress = useRef(false);

    const fetchProducts = async () => {

        if (fetchInProgress.current) {
            return;
        }

        const request = {
            eidr: selectorEidr,
            coveredProduct: selectorCoveredProduct,
            episodeName: selectorEpisodeName,
            externalId: selectorExternalId,
            financialCode: selectorFinancialCode,
            genre: selectorGenre,
            initMkt: selectorInitMkt,
            m4mktGroup: selectorM4mktGroup,
            productionType: selectorProductionType,
            producer: selectorProducer,
            name: selectorName,
            photoStartDateFrom: selectorPhotoStartDateFrom,
            photoStartDateTo: selectorPhotoStartDateTo,
            seasonExternalId: selectorSeasonExternalId,
            seriesExternalId: selectorSeriesExternalId,
            seasonName: selectorSeasonName,
            seriesName: selectorSeriesName,
            seasonNumberFrom: selectorSeasonNumberFrom,
            seasonNumberTo: selectorSeasonNumberTo,
            participations: selectorParticipations,
            residuals: selectorResiduals,
            royalties: selectorRoyalties
        };

        // check if at least one value exists
        const hasSearchCriteria = Object.values(request).some(
            value =>
                value !== null &&
                value !== undefined &&
                value !== ''
        );

        if (!hasSearchCriteria) {

            console.log("search triggered, nothing to search.");
            dispatchData(changeExternalData({ snackbarOpen: true, snackbarSeverity: "warning", snackbarMessage: "No search criteria provided" }));
            return;

        }

        fetchInProgress.current = true;
        dispatchData(changeExternalData({ submitting: true }));

        try {

            console.log("Product Search Is Triggered");
            console.log("selectorInitMkt:", selectorInitMkt);

            const response = await productSearch(request);

            console.log(response);

            setProducts(response);

            if (!response || response.length === 0) {
                console.log("no matching records found");
                dispatchData(changeExternalData({
                    snackbarOpen: true,
                    snackbarSeverity: "info",
                    snackbarMessage: "No matching records found"
                }));
            }

        } catch (error) {

            console.error(error);

        } finally {
            dispatchData(changeExternalData({ submitting: false }));
            fetchInProgress.current = false;

        }
    };
    // const fetchInProgress = useRef(false);
    //
    // const fetchProducts = () => {
    //     if (fetchInProgress.current) {
    //         return;
    //     }
    //     fetchInProgress.current = true;
    //     if (
    //         selectorEidr !== null ||
    //         selectorCoveredProduct !== null ||
    //         selectorEpisodeName !== null ||
    //         selectorExternalId !== null ||
    //         selectorFinancialCode !== null ||
    //         selectorGenre !== null ||
    //         selectorInitMkt !== null ||
    //         selectorM4mktGroup !== null ||
    //         selectorProductionType !== null ||
    //         selectorProducer !== null ||
    //         selectorName !== null ||
    //         selectorPhotoStartDateFrom !== null ||
    //         selectorPhotoStartDateTo !== null ||
    //         selectorSeasonExternalId !== null ||
    //         selectorSeriesExternalId !== null ||
    //         selectorSeasonName !== null ||
    //         selectorSeriesName !== null ||
    //         selectorSeasonNumberFrom !== null ||
    //         selectorSeasonNumberTo !== null ||
    //         selectorParticipations !== null ||
    //         selectorResiduals !== null ||
    //         selectorRoyalties !== null
    //     ) {
    //         {
    //             console.log("Product Search Is Triggered");
    //             console.log("selectorInitMkt:", selectorInitMkt);
    //             productSearch(
    //                 selectorEidr,
    //                 selectorCoveredProduct,
    //                 selectorEpisodeName,
    //                 selectorExternalId,
    //                 selectorFinancialCode,
    //                 selectorGenre,
    //                 selectorInitMkt,
    //                 selectorM4mktGroup,
    //                 selectorProductionType,
    //                 selectorProducer,
    //                 selectorName,
    //                 selectorPhotoStartDateFrom,
    //                 selectorPhotoStartDateTo,
    //                 selectorSeasonExternalId,
    //                 selectorSeriesExternalId,
    //                 selectorSeasonName,
    //                 selectorSeriesName,
    //                 selectorSeasonNumberFrom,
    //                 selectorSeasonNumberTo,
    //                 selectorParticipations,
    //                 selectorResiduals,
    //                 selectorRoyalties
    //             )

    //                 .then(data => {
    //                         console.log(data);
    //                         setProducts(data);
    //                     }
    //                 )
    //
    //         }
    //
    //     } else {
    //         console.log("search triggered, nothing to search.")
    //     }
    // .
    //     finally
    //     {
    //         fetchInProgress.current = false;
    //     }
    // }

    // ****************** END FETCH ****************************

    const handleUploadFileDrawer = () => {
        console.log("upload file drawer state changed");
        dispatchData(changeUploadFile({uploadFileDrawerState: true,
            uploadFileDrawerMode: "Product"}));
    };


    function handleSearch(){
        setShowSearch(!showSearch)
    }
    //
    // useEffect(() => {
    //     console.log("product grid use effect triggered.")
    //     fetchProducts();
    //     dispatchData(changeProduct({productGridRefresh: false}));
    // }, [useSelector((state) => state.productSlc.value.productGridRefresh)===true]);

    useEffect(() => {

        if (selectorProductGridRefresh) {

            fetchProducts();

            dispatchData(changeProduct({
                productGridRefresh: false
            }));
        }

    }, [selectorProductGridRefresh]);

    // ****************** BEGIN CRUD ***********************************

    const handleAdd = () => {
        dispatchData(changeProduct({id: null, externalId: null, name: null, episodeName: null,
            seasonExternalId: null, season: null, seasonName: null, seriesExternalId: null,
            seriesName: null,derivativeOfProductId:null,derivativeOfSeriesId:null,photoStartDate:null,
            lengthMinutes: null, m4mktGroup: null, initMkt: null, genre: null, productionType: null,
            broadcastsPerWeek: null, budget: null, productionCost: null, producer: null, financialCode: null,
            eidr: null, coveredProduct: null, status: null,
            createdOn: null,createdBy:null,lastModifiedOn:null,lastModifiedBy:null,
            productDrawerState: true, productDrawerMode: "Add"}));
    };


    const removeProduct = () => {
        deleteProduct(selectorProductId).then(() => {
            // successNotification("Data Point Attribute deleted", `Data Point Attribute with id ${selectorProductId} was deleted`);
            setDeleteOpen(false);
            dispatchData(changeProduct({productGridRefresh: true}));
            dispatchData(changeCrudSnackbar({crudOpen: true, crudSeverity: "success", crudMessage: "Record deleted"}));
        })
    }

    const handleDeleteOpen = () => {
        setDeleteOpen(true);
    };


    function DeleteConfirmationDialog() {
        // const [open, setOpen] = useState(false);

        const handleClickOpen = () => {
            setDeleteOpen(true);
        };

        const handleClose = () => {
            setDeleteOpen(false);
        };


        const handleDelete = () => {
            // Place your delete logic here
            deleteProduct(selectedId).then(() => {
                // successNotification("Data Point Attribute deleted", `Data Point Attribute with id ${selectorFinancialId} was deleted`);
                setDeleteOpen(false);
                dispatchData(changeProduct({productGridRefresh: true}));
                dispatchData(changeCrudSnackbar({crudOpen: true, crudSeverity: "success", crudMessage: "Record deleted"}));
            })
            console.log('Item deleted');
        };

        return (
            <Dialog
                open={deleteOpen}
                onClose={handleClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                sx={{
                    '& .MuiBackdrop-root': {
                        backgroundColor: 'transparent', // makes backdrop transparent
                        boxShadow: '0 0 10px 5px rgba(0, 0, 0, 0.5)' // custom shadow box effect
                    }
                }}
            >
                <DialogTitle id="alert-dialog-title">{"Confirm Delete"}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Are you sure you want to delete this item? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button onClick={handleDelete} autoFocus color="error">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }
    const handleDeleteCancel = () => {
        setDeleteOpen(false);
    };

    // ****************** END CRUD ***********************************


    const handleMousedRow = (products) => {

        console.log('Product data from clicked row');
        console.log(products);
        dispatchData(changeProduct({id: products.id, externalId: products.externalId,
            name: products.name,  episodeName: products.episodeName,
            seasonExternalId: products.seasonExternalId, season: products.season, seasonName: products.seasonName,
            seriesExternalId: products.seriesExternalId, seriesName: products.seriesName,
            derivativeOfProductId:products.derivativeOfProductId,derivativeOfSeriesId:products.derivativeOfSeriesId,
            photoStartDate:products.photoStartDate, lengthMinutes: products.lengthMinutes,
            m4mktGroup: products.m4mktGroup, initMkt: products.initMkt, genre: products.genre, productionType: products.productionType,
            broadcastsPerWeek: products.broadcastsPerWeek, budget: products.budget, productionCost: products.productionCost,
            producer: products.producer, financialCode: products.financialCode,
            eidr: products.eidr, coveredProduct: products.coveredProduct, status: products.status,
            createdOn: products.createdOn,createdBy:products.createdBy,
            lastModifiedOn:products.lastModifiedOn,lastModifiedBy:products.lastModifiedBy}));
    };

    function CrudMenu() {

        const [anchorEl, setAnchorEl] = React.useState(null);

        const isMenuOpen = Boolean(anchorEl);

        const handleCrudMenuOpen = (event) => {
            setAnchorEl(event.currentTarget);
        };

        function handleMenuClose()  {
            setAnchorEl(null);

        };

        const handleEdit = () => {
            dispatchData(changeProduct({productDrawerState: true,
                productDrawerMode: "Edit"}));
        };

        const handleClone = () => {
            dispatchData(changeProduct({productDrawerState: true,
                productDrawerMode: "Clone"}));
        };


        const crudId = 'primary-search-account-menu';

        const renderMenu = (
            <Menu
                anchorEl={anchorEl}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                id={crudId}
                keepMounted
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                open={isMenuOpen}
                onClose={handleMenuClose}
            >

                <MenuItem
                    onClick={handleEdit}
                >
                    <ListItemIcon>
                        <EditIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Edit</ListItemText>
                </MenuItem>
                <MenuItem
                    onClick={handleClone}
                >
                    <ListItemIcon>
                        <PeopleAltIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Clone</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleDeleteOpen}>
                    <ListItemIcon>
                        <DeleteOutlineIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Delete</ListItemText>
                </MenuItem>
            </Menu>
        );
        return (
            <div>
                <IconButton
                    size="large"
                    edge="end"
                    aria-label="account of current user"
                    aria-controls={crudId}
                    aria-haspopup="true"
                    onClick={handleCrudMenuOpen}
                    color="inherit"
                >
                    <MoreVertIcon  color="primary" fontSize="small"/>
                </IconButton>
                {renderMenu}
            </div>
        );
    }

    // function ResidualMenu({row}) {
    //
    //     const [residualAnchorEl, setResidualAnchorEl] = React.useState(null);
    //
    //     const isResidualMenuOpen = Boolean(residualAnchorEl);
    //
    //     const handleResidualMenuOpen = (event) => {
    //         setResidualAnchorEl(event.currentTarget);
    //     };
    //
    //     function handleResidualMenuClose()  {
    //         setResidualAnchorEl(null);
    //
    //     };
    //
    //     const handleAddGuilds = () => {
    //         dispatchData(changeProduct({guildMultiSelectState: true,
    //             bulkState: false}));
    //         console.log("guild dialogue should open now")
    //     };
    //
    //     const handleCloneResiduals = () => {
    //     };
    //
    //     const handleResidualsList = () => {
    //     };
    //
    //     const handleGoToResiduals = (row) => {
    //         console.log("series Id to expand from product: ", selectorSeriesId);
    //         console.log("season Id to expand from product: ", selectorSeasonId);
    //         console.log("season number to expand from product: ", selectorSeasonNumber);
    //         console.log("product Id to expand from product: ", selectorProductId);
    //         dispatchData(changeWorkQueue({contractExpand: true,
    //             contractExpandHierarchical: selectorSeriesId !== null,
    //             contractExpandSeriesId: selectorSeriesId,
    //             contractExpandSeasonId: selectorSeasonId,
    //             contractExpandSeasonNumber: selectorSeasonNumber,
    //             contractExpandProductId: selectorProductId
    //             }));
    //         navigate('/Work');
    //     };
    //
    //     const handleCovered = () => {
    //         console.log("updating this product", selectorProductId);
    //         updateCoveredProduct(selectorProductId, "Yes")
    //             .then(() => {
    //         dispatchData(changeProduct({productGridRefresh: true}));})
    //     };
    //
    //     const handleNotCovered = () => {
    //         console.log("updating this product", selectorProductId);
    //         updateCoveredProduct(selectorProductId, "No")
    //             .then(() => {
    //                 dispatchData(changeProduct({productGridRefresh: true}));})
    //     };
    //
    //     const handleResearch = () => {
    //         console.log("updating this product", selectorProductId);
    //         updateCoveredProduct(selectorProductId, "Research")
    //             .then(() => {
    //                 dispatchData(changeProduct({productGridRefresh: true}));})
    //     };
    //
    //     const handleNoInfo = () => {
    //         console.log("updating this product", selectorProductId);
    //         updateCoveredProduct(selectorProductId, "No Info")
    //             .then(() => {
    //                 dispatchData(changeProduct({productGridRefresh: true}));})
    //     };
    //
    //     const handleClearInfo = () => {
    //         console.log("updating this product", selectorProductId);
    //         updateCoveredProduct(selectorProductId, "null")
    //             .then(() => {
    //                 dispatchData(changeProduct({productGridRefresh: true}));})
    //     };
    //
    //     const residualId = 'residual_menu';
    //
    //     const renderResidualMenu = (
    //         <Menu
    //             anchorEl={residualAnchorEl}
    //             anchorOrigin={{
    //                 vertical: 'top',
    //                 horizontal: 'right',
    //             }}
    //             id={residualId}
    //             keepMounted
    //             transformOrigin={{
    //                 vertical: 'top',
    //                 horizontal: 'right',
    //             }}
    //             open={isResidualMenuOpen}
    //             onClose={handleResidualMenuClose}
    //         >
    //
    //             <MenuItem
    //                 onClick={handleAddGuilds}
    //             >
    //                 <ListItemIcon>
    //                     <AddIcon fontSize="small" />
    //                 </ListItemIcon>
    //                 <ListItemText>Add Guilds</ListItemText>
    //             </MenuItem>
    //             <MenuItem
    //                 onClick={handleCloneResiduals}
    //             >
    //                 <ListItemIcon>
    //                     <PeopleAltIcon fontSize="small" />
    //                 </ListItemIcon>
    //                 <ListItemText>Clone Contracts</ListItemText>
    //             </MenuItem>
    //             <MenuItem onClick={handleGoToResiduals}>
    //                 <ListItemIcon>
    //                     <ForwardIcon fontSize="small" />
    //                 </ListItemIcon>
    //                 <ListItemText>Go To Contracts</ListItemText>
    //             </MenuItem>
    //             <MenuItem onClick={handleResidualsList}>
    //                 <ListItemIcon>
    //                     <ListIcon fontSize="small" />
    //                 </ListItemIcon>
    //                 <ListItemText>Contracts List</ListItemText>
    //             </MenuItem>
    //             <Divider/>
    //             <MenuItem onClick={handleCovered}>
    //                 <ListItemIcon>
    //                     <CheckCircleIcon fontSize="small" />
    //                 </ListItemIcon>
    //                 <ListItemText>Covered</ListItemText>
    //             </MenuItem>
    //                 <MenuItem onClick={handleNotCovered}>
    //                     <ListItemIcon>
    //                         <NotInterestedIcon fontSize="small" />
    //                     </ListItemIcon>
    //                     <ListItemText>Not Covered</ListItemText>
    //                 </MenuItem>
    //                     <MenuItem onClick={handleResearch}>
    //                         <ListItemIcon>
    //                             <SearchIcon fontSize="small" />
    //                         </ListItemIcon>
    //                         <ListItemText>Research</ListItemText>
    //                     </MenuItem>
    //                         <MenuItem onClick={handleNoInfo}>
    //                             <ListItemIcon>
    //                                 <HelpIcon fontSize="small" />
    //                             </ListItemIcon>
    //                             <ListItemText>No Info</ListItemText>
    //                         </MenuItem>
    //             <MenuItem onClick={handleClearInfo}>
    //                 <ListItemIcon>
    //                     <HighlightOffIcon fontSize="small" />
    //                 </ListItemIcon>
    //                 <ListItemText>Clear</ListItemText>
    //             </MenuItem>
    //         </Menu>
    //     );
    //
    //     return (
    //         <div>
    //             <Tooltip enterDelay={1000} title={
    //                 row.residuals > 0 ? "Residuals contracts exist on this title" :
    //                     row.coveredProduct === "Yes" ? "Covered Product" :
    //                         row.coveredProduct === "No" ? "Not Covered" :
    //                             row.coveredProduct === "Research" ? "Research Needed" :
    //                                 row.coveredProduct === "No Info" ? "No Info Available" :
    //                      "Undetermined"}>
    //                 <Avatar
    //                     onClick={handleResidualMenuOpen}
    //                     sx={{ height: 23, width: 23, fontSize: 14, bgcolor:
    //                             row.residuals > 0 ? "rgb(30,144,255)" :
    //                                 row.coveredProduct === "Yes" ? "green" :
    //                                     row.coveredProduct === "No" ? "black" :
    //                                         row.coveredProduct === "Research" ? "orange" :
    //                                             row.coveredProduct === "No Info" ? "red" :
    //                                     "grey",
    //                         opacity:
    //                             row.residuals > 0 ? 1 :
    //                                 row.coveredProduct === "Yes" ? 1:
    //                                     row.coveredProduct === "No" ? 1 :
    //                                         row.coveredProduct === "Research" ? 1 :
    //                                             row.coveredProduct === "No Info" ? 1 :
    //                                  0.5
    //                     }}
    //                 >
    //                     {row.residuals > 0 ? "Re" :
    //                         row.coveredProduct === "Yes" ? "C":
    //                             row.coveredProduct === "No" ? "N" :
    //                                 row.coveredProduct === "Research" ? "R" :
    //                                     row.coveredProduct === "No Info" ? "?" :
    //                                          ""}
    //                 </Avatar>
    //             </Tooltip>
    //             {renderResidualMenu}
    //         </div>
    //     );
    //
    // }

//     function GuildDialog({
//                              selectorGuildMultiSelect,
//                              handleCancel
//                          }) {
//         const [selectedGuilds, setSelectedGuilds] = useState([]);
// const openGuild = selectorGuildMultiSelect;
//
// const handleGuildSubmit = () => {
//     if(selectorBulkState === false)
//     {
//         dispatchData(changeProduct({guildMultiSelectState: false}));
//         setSubmitting(true);
//
//         console.log("what product are we updating?");
//         console.log(selectorProductId);
//         console.log("what guilds are we submitting?");
//         console.log(selectedGuilds);
//         console.log("guilds stringified");
//         console.log(selectedGuilds.join(', '));
//         addGuildContracts(selectorProductId, selectedGuilds.join(','))
//             .then(() => {
//                 dispatchData(changeProduct({productGridRefresh: true}));
//                 setSubmitting(false);
//                 console.log("Successfully added guilds!")
//             })
//     }
//     else {        dispatchData(changeProduct({guildMultiSelectState: false}));
//         setSubmitting(true);
//
//         console.log("what products are we updating?");
//         console.log(selectedRows);
//         console.log("products stringified");
//         console.log(selectedRows.join(', '));
//         console.log("what guilds are we submitting?");
//         console.log(selectedGuilds);
//         console.log("guilds stringified");
//         console.log(selectedGuilds.join(', '));
//         addGuildContractsBulk (selectedRows.join(','), selectedGuilds.join(','))
//             .then(() => {
//                 dispatchData(changeProduct({productGridRefresh: true}));
//                 setSubmitting(false);
//                 console.log("Successfully added guilds in bulk!")
//             })}
// }
//
//         const handleGuildCancel = () => {
//             dispatchData(changeProduct({guildMultiSelectState: false}));
//             console.log("guild dialogue close open now")
//         };
//
//         return (
//             <Dialog
//                 open={openGuild}
//                 onClose={handleGuildCancel} // This will close the dialog when clicking outside or pressing ESC
//                 // maxWidth="xs" // Controls the maximum width of the dialog
//                 // fullWidth // Makes the dialog take up the full width allowed by maxWidth
//             >
//                 <Box sx={{ padding: 2, width: 500, height: 180 }}> {/* Adds some padding inside the dialog */}
//                     <p>{selectorBulkState === false ?
//                         `Add guild contracts to Product ID: ${selectorProductId}` :
//                         'Add guild contracts to selected products'}</p>
//                         <GuildMultiSelect
//                         selectedGuilds={selectedGuilds}
//                         setSelectedGuilds={setSelectedGuilds}
//                     />
//                     <Box
//                         sx={{
//                             display: 'flex',
//                             justifyContent: 'flex-end',
//                             marginTop: 2,
//                             gap: 1,
//                         }}
//                     >
//                         <Button
//                             type="submit"
//                             variant="contained"
//                             color="primary"
//                             onClick={handleGuildSubmit} // Connects the submit button to the submit handler
//                         >
//                             Submit
//                         </Button>
//                         <Button
//                             variant="outlined"
//                             // color="secondary"
//                             onClick={handleGuildCancel} // Connects the cancel button to the cancel handler
//                         >
//                             Cancel
//                         </Button>
//                     </Box>
//                 </Box>
//             </Dialog>
//         );
//     }

    // ****************** BEGIN HEADER MENU ****************************



    function ResidualHeaderMenu() {

        const [residualHeaderAnchorEl, setResidualHeaderAnchorEl] = React.useState(null);

        const isResidualHeaderMenuOpen = Boolean(residualHeaderAnchorEl);

        const handleResidualHeaderMenuOpen = (event) => {
            setResidualHeaderAnchorEl(event.currentTarget);
        };

        function handleResidualHeaderMenuClose() {
            setResidualHeaderAnchorEl(null);

        };

        const handleAddGuildsBulk = () => {
            dispatchData(changeProduct({guildMultiSelectState: true,
                bulkState: true}));
            
            console.log("guild dialogue should open now")
            // dispatchData(changeProductControls({productDrawerState: true,
            //     productDrawerMode: "Edit"}));
        };

        const handleCoveredBulk = () => {
            console.log("updating this product", selectorProductId);
            updateCoveredProduct(selectorProductId, "Yes")
                .then(() => {
                    dispatchData(changeProduct({productGridRefresh: true}));
                })
        };

        const handleNotCoveredBulk = () => {
            console.log("updating this product", selectorProductId);
            updateCoveredProduct(selectorProductId, "No")
                .then(() => {
                    dispatchData(changeProduct({productGridRefresh: true}));
                })
        };

        const handleResearchBulk = () => {
            console.log("updating this product", selectorProductId);
            updateCoveredProduct(selectorProductId, "Research")
                .then(() => {
                    dispatchData(changeProduct({productGridRefresh: true}));
                })
        };

        const handleNoInfoBulk = () => {
            console.log("updating this product", selectorProductId);
            updateCoveredProduct(selectorProductId, "No Info")
                .then(() => {
                    dispatchData(changeProduct({productGridRefresh: true}));
                })
        };

        const handleClearInfoBulk = () => {
            console.log("updating this product", selectorProductId);
            updateCoveredProduct(selectorProductId, "null")
                .then(() => {
                    dispatchData(changeProduct({productGridRefresh: true}));
                })
        };

        const residualHeaderId = 'residualHeader_menu';

        const renderResidualHeaderMenu = (
            <Menu
                anchorEl={residualHeaderAnchorEl}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                id={residualHeaderId}
                keepMounted
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                open={isResidualHeaderMenuOpen}
                onClose={handleResidualHeaderMenuClose}
            >
{/*<p>Bulk Update</p>*/}
                <MenuItem
                    onClick={handleAddGuildsBulk}
                >
                    <ListItemIcon>
                        <AddIcon fontSize="small"/>
                    </ListItemIcon>
                    <ListItemText>Add Guilds</ListItemText>
                </MenuItem>
                <Divider/>
                <MenuItem onClick={handleCoveredBulk}>
                    <ListItemIcon>
                        <CheckCircleIcon fontSize="small"/>
                    </ListItemIcon>
                    <ListItemText>Covered</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleNotCoveredBulk}>
                    <ListItemIcon>
                        <NotInterestedIcon fontSize="small"/>
                    </ListItemIcon>
                    <ListItemText>Not Covered</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleResearchBulk}>
                    <ListItemIcon>
                        <SearchIcon fontSize="small"/>
                    </ListItemIcon>
                    <ListItemText>Research</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleNoInfoBulk}>
                    <ListItemIcon>
                        <HelpIcon fontSize="small"/>
                    </ListItemIcon>
                    <ListItemText>No Info</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleClearInfoBulk}>
                    <ListItemIcon>
                        <HighlightOffIcon fontSize="small"/>
                    </ListItemIcon>
                    <ListItemText>Clear</ListItemText>
                </MenuItem>
            </Menu>
        );

        return (
            <div>
                <p
                    onClick={handleResidualHeaderMenuOpen} style={{cursor:'pointer'}}
                >Re</p>
                {renderResidualHeaderMenu}
            </div>
        );
    }


        const [selectedRows, setSelectedRows] = useState([]);

        const handleSelectionChange = (newSelection) => {
            setSelectedRows(newSelection);
            console.log(newSelection);
            console.log(selectedRows);
        };


    const gridClear = () => {

        setProducts([]);
    }


    const handleProductInfo = () => {
        setProductInfoOpen(true);
    };

    const handleCloseProductInfo = () => {
        setProductInfoOpen(false);
    };





    // ****************** BEGIN RETURN ****************************

    return (
        <div style={{ height:800 , width: '100%' }}>
            <Box sx={{height: 80, pt: 1, pl: 3}}>
                <Stack direction={"horizontal"}>
                    <Tooltip enterDelay={1000} enterNextDelay={1000} title={"Click for information."}>
                    <h3
                        style={{cursor:'pointer'}}   onClick={() => handleProductInfo()}
                    >Products</h3>
                    </Tooltip>
                        <ProductInfoDialogue
                            open={productInfoOpen}
                            onClose={handleCloseProductInfo}
                />
                    {/*<SearchIcon*/}
                    {/*    sx={{pt: 2, ml: 2}}*/}
                    {/*    color={'primary'}*/}
                    {/*    onClick={handleSearch}*/}
                    {/*/>*/}
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton
                            color="primary"
                            size="large"               // large icon & hit area
                            onClick={handleSearch}
                            sx={{ ml: 2 }}             // any spacing you want
                        >
                            <SearchIcon fontSize="inherit" />
                        </IconButton>
                    </Box>
                    <Button sx={{pt: 1, ml: 3}}
                            onClick={handleUploadFileDrawer}
                    >Upload</Button>
                </Stack>
            </Box>
            {showSearch && <ProductSearch
                gridClear = {gridClear}
            />}
            {selectorUploadFileDrawerState && <UploadFileDrawer/>}
            {selectorDrawerState && <ProductDrawer
            />}


            {submitting && <LinearIndeterminate/>}
            {gridVisible && <DataGrid
                experimentalFeatures={{ newEditingApi: true }}
                initialState={{
                }}
                aria-label={"Products"}
                rows={products}
                disableDensitySelector={true}
                disableColumnSelector={true}
                // columns={columns(fetchProducts)}
                columns={ProductGridColumns({
                    products,
                    ResidualHeaderMenu,
                    handleMousedRow,
                    hideActions,
                    onEdit: () => dispatchData(changeProduct({ productDrawerState: true, productDrawerMode: "Edit" })),
                    onClone: () => dispatchData(changeProduct({ productDrawerState: true, productDrawerMode: "Clone" })),
                    onDelete: removeProduct
                })}
                density={"compact"}
                pageSize={20}
                rowsPerPageOptions={[20]}
                rowKey={product => product.id}
                checkboxSelection
                disableSelectionOnClick
                onSelectionModelChange={(newSelectionModel) => {
                    handleSelectionChange(newSelectionModel);
                }}
                selectionModel={selectedRows}
                components={{
                    Toolbar: AddFiltersExportToolbar,
                    Footer: CustomFooter,
                    BaseCheckbox: (props) => (
                        <Checkbox
                            {...props}
                            size="small" // Make the checkbox small
                        />
                    ),
                }}
                componentsProps={{
                    toolbar: {
                        handleAdd: handleAdd
                    }
                }}
            />}
            <ProductGuildDialogue
                selectorGuildMultiSelect={selectorGuildMultiSelect}
                selectorProductId={selectorProductId}
            />
            <CrudSnackbar/>
            <NoSearchCriteriaSnackbar/>
        </div>

    );

    // ****************** END RETURN ****************************

}
